import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FormModal, useToastActions, LoadingSpinner, Select } from '@airclou/ui';
import { getAppDefaultMemberRole } from '@airclou/utils';
import { addWorkspaceApp, listAvailableApps } from '@/api/workspaces';
import { AppWindow } from 'lucide-react';

interface AddAppToWorkspaceModalProps {
  workspaceId: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface AvailableApp {
  id: string;
  name: string;
  slug: string;
}

export function AddAppToWorkspaceModal({ workspaceId, isOpen, onClose, onSuccess }: AddAppToWorkspaceModalProps) {
  const { t } = useTranslation('dashboard');
  const { success, error } = useToastActions();
  const [loading, setLoading] = useState(false);
  const [fetchingApps, setFetchingApps] = useState(true);
  const [availableApps, setAvailableApps] = useState<AvailableApp[]>([]);
  const [selectedAppId, setSelectedAppId] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadAvailableApps();
    }
  }, [isOpen, workspaceId]);

  const loadAvailableApps = async () => {
    try {
      setFetchingApps(true);
      const response = await listAvailableApps(workspaceId);
      setAvailableApps(response.apps);
      if (response.apps.length > 0) {
        setSelectedAppId(response.apps[0].id);
      }
    } catch (err: any) {
      error(err.message || t('workspaceTabs.failedToAddApp'), t('common.error'));
    } finally {
      setFetchingApps(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedAppId) {
      error(t('workspaceTabs.selectApp'), t('common.error'));
      return;
    }

    setLoading(true);

    try {
      // Get the default member role for this app (e.g., 'author' for playbook)
      const selectedApp = availableApps.find(app => app.id === selectedAppId);
      const defaultMemberRole = getAppDefaultMemberRole(selectedApp?.slug);

      await addWorkspaceApp(workspaceId, selectedAppId, defaultMemberRole);
      success(t('workspaceTabs.appAddedToWorkspace'), t('common.success'));
      onSuccess();
      onClose();
    } catch (err: any) {
      error(err.message || t('workspaceTabs.failedToAddApp'), t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const canSubmit = !!selectedAppId && availableApps.length > 0;

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={t('workspaceTabs.addAppToWorkspace')}
      size="md"
      submitText={loading ? t('common.adding') : t('workspaceTabs.addApp')}
      canSubmit={canSubmit && !loading && !fetchingApps}
      onSubmit={handleSubmit}
    >
      {fetchingApps ? (
        <div className="py-8 text-center">
          <LoadingSpinner text={t('workspaceTabs.loadingApps')} textPosition="bottom" centerVertically />
        </div>
      ) : availableApps.length === 0 ? (
        <div className="py-8 text-center">
          <AppWindow className="w-12 h-12 mx-auto text-gray-400 dark:text-gray-600 mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">
            {t('workspaceTabs.noAppsAvailable')}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('workspaceTabs.allAppsAdded')}
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          <div>
            <label htmlFor="app-select" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('workspaceTabs.selectApp')}
            </label>
            <Select
              id="app-select"
              value={selectedAppId}
              onChange={setSelectedAppId}
              className="w-full"
              options={availableApps.map((app) => ({
                value: app.id,
                label: `${app.name} (${app.slug})`,
              }))}
            />
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              {t('workspaceTabs.chooseApp')}
            </p>
          </div>

          {selectedAppId && (
            <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
              <div className="flex items-start gap-3">
                <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30">
                  <AppWindow className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-foreground">
                    {availableApps.find(app => app.id === selectedAppId)?.name}
                  </h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {availableApps.find(app => app.id === selectedAppId)?.slug}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </FormModal>
  );
}
